import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { sanitize } from '../lib/sanitize';
import { useStoreSettings } from '../hooks/useStoreSettings';
import SeoHead from '../components/seo/SeoHead';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image: string | null;
  published_at: string | null;
  created_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_image_url: string | null;
  noindex: boolean | null;
}

const formatDate = (d: string | null): string => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { settings, loading: settingsLoading } = useStoreSettings();
  const blogEnabled = (settings?.header_content as any)?.showBlog === true;
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, content, featured_image, published_at, created_at, seo_title, seo_description, seo_image_url, noindex')
          .eq('slug', slug)
          .eq('status', 'published')
          .maybeSingle();
        if (error) throw error;
        setPost(data);
      } catch (err) {
        console.error('Error loading blog post:', err);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchPost();
  }, [slug]);

  const wrap: React.CSSProperties = { maxWidth: 800, margin: '0 auto', padding: '32px 20px' };

  if (!settingsLoading && !blogEnabled) {
    return (
      <div style={wrap}>
        <SeoHead title="Blog" noindex />
        <p>Blog trenutno nije dostupan.</p>
        <Link to="/">Nazad na početnu</Link>
      </div>
    );
  }

  if (loading) {
    return <div style={wrap}><p>Učitavanje...</p></div>;
  }

  if (!post) {
    return (
      <div style={wrap}>
        <SeoHead title="Članak nije pronađen" noindex />
        <h1>Članak nije pronađen</h1>
        <p>Tekst koji tražiš ne postoji ili je uklonjen.</p>
        <Link to="/blog">Nazad na blog</Link>
      </div>
    );
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const absImg = post.featured_image
    ? (post.featured_image.startsWith('http') ? post.featured_image : origin + post.featured_image)
    : undefined;
  const storeName = (settings as any)?.store_name || 'saksije.serbia-shop.com';
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt || undefined,
    image: absImg ? [absImg] : undefined,
    datePublished: post.published_at || post.created_at || undefined,
    dateModified: post.published_at || post.created_at || undefined,
    author: { '@type': 'Organization', name: storeName },
    publisher: { '@type': 'Organization', name: storeName },
    mainEntityOfPage: origin ? `${origin}/blog/${post.slug}` : undefined,
  };

  return (
    <div style={wrap}>
      <SeoHead
        title={post.title}
        titleOverride={post.seo_title}
        description={post.seo_description || post.excerpt || post.content || ''}
        image={post.seo_image_url || post.featured_image || undefined}
        type="article"
        noindex={Boolean(post.noindex)}
        jsonLd={articleLd}
      />
      <Link to="/blog" style={{ fontSize: 14, color: '#2563eb', textDecoration: 'none' }}>
        ← Nazad na blog
      </Link>
      <h1 style={{ margin: '16px 0 8px', lineHeight: 1.25 }}>{post.title}</h1>
      {(post.published_at || post.created_at) && (
        <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 20 }}>
          {formatDate(post.published_at || post.created_at)}
        </div>
      )}
      {post.featured_image && (
        <img
          src={post.featured_image}
          alt={post.title}
          style={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 10, marginBottom: 24 }}
        />
      )}
      <article
        style={{ lineHeight: 1.7, fontSize: 16, color: '#1f2937' }}
        dangerouslySetInnerHTML={{ __html: sanitize(post.content) }}
      />
    </div>
  );
};

export default BlogPost;
